import React, { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { chatFeedbackApi, TurnRating } from '../../../services/api/chatFeedback.api';
import { useClickOutside } from '../../../hooks/useClickOutside';

export type { TurnRating };

const UP_PHRASES = ['Accurate', 'Helpful', 'Clear & well formatted'];
const DOWN_PHRASES = ['Inaccurate', 'Not helpful', 'Incomplete or missing info'];

// Gap between the thumbs and the popover (matches the CSS offset).
const POPOVER_GAP = 8;

// The visible area the popover has to fit in: the nearest scrolling ancestor
// (the message list), trimmed to the viewport. Anything outside it is cut
// off by the list's overflow or sits behind the chat input below it.
const getVisibleBounds = (el: HTMLElement): { top: number; bottom: number } => {
  let top = 0;
  let bottom = window.innerHeight;
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      const rect = node.getBoundingClientRect();
      top = Math.max(top, rect.top);
      bottom = Math.min(bottom, rect.bottom);
      break;
    }
  }
  return { top, bottom };
};

interface TurnFeedbackProps {
  turnId: string;
  sessionId: string | null;
  // The rating already on record for this turn, lifted up to Chat.tsx so it
  // survives history pagination/scrolling within the current page session —
  // see Chat.tsx's turnRatings map for why this isn't local state here.
  rating?: TurnRating;
  onRated: (turnId: string, rating: TurnRating) => void;
}

const TurnFeedback: React.FC<TurnFeedbackProps> = ({ turnId, sessionId, rating, onRated }) => {
  const [openPanel, setOpenPanel] = useState<TurnRating | null>(null);
  const [selectedPhrases, setSelectedPhrases] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [openAbove, setOpenAbove] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // wrapRef holds both thumb buttons and the panel, so the thumbs count as inside
  useClickOutside([wrapRef], () => setOpenPanel(null), openPanel !== null);

  // Opens below the thumbs by default. When there isn't room for it below
  // (e.g. the last message of a long chat, right above the input), it opens
  // above them instead, unless there's even less room up there. Measured
  // before paint, so it never shows in the wrong spot first.
  useLayoutEffect(() => {
    if (!openPanel || !wrapRef.current || !popoverRef.current) {
      setOpenAbove(false);
      return;
    }
    const thumbs = wrapRef.current.getBoundingClientRect();
    const needed = popoverRef.current.offsetHeight + POPOVER_GAP;
    const bounds = getVisibleBounds(wrapRef.current);
    const roomBelow = bounds.bottom - thumbs.bottom;
    const roomAbove = thumbs.top - bounds.top;
    setOpenAbove(roomBelow < needed && roomAbove > roomBelow);
  }, [openPanel]);

  const togglePanel = (nextRating: TurnRating) => {
    setOpenPanel((prev) => (prev === nextRating ? null : nextRating));
    setSelectedPhrases([]);
    setCustomText('');
  };

  const togglePhrase = (phrase: string) => {
    setSelectedPhrases((prev) =>
      prev.includes(phrase) ? prev.filter((p) => p !== phrase) : [...prev, phrase]
    );
  };

  const handleSubmit = async () => {
    if (!openPanel || submitting) return;

    if (!sessionId) {
      toast.error('Something went wrong, please try again!');
      return;
    }

    setSubmitting(true);

    try {
      await chatFeedbackApi.submitTurnFeedback({
        turn_id: turnId,
        session_id: sessionId,
        chat_response_rating: openPanel,
        feedback_defined_phrases: selectedPhrases,
        custom_feedback: customText.trim() || null,
      });
      onRated(turnId, openPanel);
      setOpenPanel(null);
      toast.success('Thanks for the feedback!');
    } catch {
      toast.error('Something went wrong, please try again!');
    } finally {
      setSubmitting(false);
    }
  };

  const phrases = openPanel === 'down' ? DOWN_PHRASES : UP_PHRASES;

  return (
    <div className="turn-feedback" ref={wrapRef}>
      <button
        type="button"
        className={`msg-action-btn${rating === 'up' ? ' msg-action-btn--rated-up' : ''}`}
        aria-label="Good response"
        title="Good response"
        onClick={() => togglePanel('up')}
      >
        <i className="fas fa-thumbs-up" />
      </button>
      <button
        type="button"
        className={`msg-action-btn${rating === 'down' ? ' msg-action-btn--rated-down' : ''}`}
        aria-label="Bad response"
        title="Bad response"
        onClick={() => togglePanel('down')}
      >
        <i className="fas fa-thumbs-down" />
      </button>

      {openPanel && (
        <div
          ref={popoverRef}
          className={`turn-feedback-popover${openAbove ? ' turn-feedback-popover--above' : ''}`}
        >
          <p className="turn-feedback-popover-title">
            {openPanel === 'up' ? 'What did you like?' : 'What went wrong?'}
          </p>

          <div className="turn-feedback-chips">
            {phrases.map((phrase) => (
              <button
                key={phrase}
                type="button"
                className={`turn-feedback-chip${selectedPhrases.includes(phrase) ? ' selected' : ''}`}
                onClick={() => togglePhrase(phrase)}
              >
                {phrase}
              </button>
            ))}
          </div>

          <textarea
            className="turn-feedback-textarea"
            placeholder="Add more details (optional)"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            maxLength={2000}
          />

          <div className="turn-feedback-actions">
            <button
              type="button"
              className="turn-feedback-action-btn"
              onClick={() => setOpenPanel(null)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="turn-feedback-action-btn primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurnFeedback;
